import { FC, useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Link, RouteComponentProps } from 'react-router-dom';
import queryString from 'query-string';
import { connect } from 'react-redux';

import { getContentLocale } from '~src/services/session';
import { SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';
import { AttributeI } from '~src/interfaces/attributes.types';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { MatrixResponse, TopRow } from '~src/interfaces/heatmap.interface';
import { fetchScales } from '~src/modules/scales/actions/scaleActions';
import { FetchScalesParams, Scale, ScaleIncludeParam, ScalesRespI } from '~src/interfaces/scale.interface';
import { fetchMatrixDistributor, setMatrixSurvey } from '~src/store/heatmap/actions';
import { StoreI } from '~src/rootReducer';
import { fetchTopicById } from '~src/modules/topics/actions/topicActions';
import {
  ApiReduxResp,
  ThunkAnyAction,
  ThunkMWDispatch,
  ThunkSuccessAction,
} from '~src/services/apiMiddleware';
import { getUrlFromObject } from '~src/utils';
import { fetchAttributes, FetchAttributesParams } from '~src/modules/attributes/actions/attributes.actions';
import { AttributesRespI } from '~src/modules/attributes/interfaces';
import { routes } from '~src/routingConfig';
import { PeriodValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import { ChangeSelectedPeriod } from '~src/modules/reports/reportsHeader/actions/headerActions.interface';
import { changeSelectedPeriod } from '~src/modules/reports/reportsHeader/actions/headerActions';
import { ESQuestion } from '~src/interfaces/question.interface';
import { ESTopic } from '~src/interfaces/topic.interface';

import RatingTable from './components/table/RatingTable';
import Header from './components/header/Header';
import { HeatmapListFilters } from './heatmap.types';
import { GlobalSpinnerContainer } from './components/table/ratingTable.sc';
import TableError from './components/table/TableError';
import { CompareContainer, NoAttrsMessage } from './heatmap.sc';
import { defaultQueryParams } from './components/header/AttributeSelector';

interface DispatchProps {
  changeSelectedPeriod: ChangeSelectedPeriod;
  fetchMatrix: (
    attribute: string,
    surveyType: SurveySubjectTypes,
    params: HeatmapListFilters,
  ) => Promise<ThunkSuccessAction   | undefined>;
  fetchTopicById: (topicId: string) => Promise<ThunkAnyAction>;
  fetchScales(params: FetchScalesParams): ApiReduxResp<ScalesRespI>;
  fetchAttrs(params: FetchAttributesParams): ApiReduxResp<AttributesRespI>;
  setSurvey(survey?: string): void;
}

interface StateProps {
  abortController?: AbortController;
  contentLocale: string;
  loadingTable: boolean;
  attributesById: { [key: string]: AttributeI };
  optionAttributes: AttributeI[];
  result: MatrixResponse | null;
  topRow?: TopRow;
  error: string | null;
  topics: ESTopic[];
  scales: Scale[];
}

const initialFilters: HeatmapListFilters = {
  attribute: null,
  surveyPulse: null,
  surveyGroup: null,
  surveyPeople: null,
  selectedGroup: null,
  filterTopicId: null,
  filterAnsweredRange: PeriodValues.AllTime,
  page: '1',
};

type FilterIds = 'attribute' | 'surveyPulse' | 'surveyGroup' | 'selectedGroup' | 'filterTopicId' | 'page';

type Props = WithTranslation & RouteComponentProps<object> & DispatchProps & StateProps;

const Heatmap: FC<Props> = (props) => {
  const [filterState, setFilterState] = useState(initialFilters);
  const [selectedSurveyType, setSelectedSurveyType] = useState<SurveySubjectTypes>(
    SurveySubjectTypes.General,
  );
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialAttrsLoading, setInitialAttrsLoading] = useState(true);
  const [fetchingError, setFetchingError] = useState(false);

  useEffect(() => {
    props
      .fetchAttrs(defaultQueryParams)
      .then(({ error }) => {
        if (error) setFetchingError(true);
      })
      .finally(() => {
        setInitialAttrsLoading(false);
      });
  }, []);

  // FIXME: this is causing requesting scales and surveys two times on initial loading!
  useEffect(() => {
    // if an empty link is clicked to reset the data
    if (!props.location.search || initialLoading) {
      if (!initialLoading) {
        setInitialLoading(true);
      }
      if (!initialAttrsLoading) {
        prepareTable().then(() => {
          setInitialLoading(false);
        });
      }
    }
  }, [props.location.search, initialAttrsLoading]);

  const prepareTable = async () => {
    const defaultFilters = initializeFromUrl();
    const promises: Array<Promise<any>> = [
      props.fetchScales({
        include: `${ScaleIncludeParam.ScaleDeleted},${ScaleIncludeParam.ScaleVersion}`,
        per_page: 1000,
      }),
    ];
    if (defaultFilters.filterTopicId) {
      promises.push(props.fetchTopicById(defaultFilters.filterTopicId));
    }

    await Promise.all(promises);
    await fetchContent(defaultFilters);
  };

  const fetchContent = (params: HeatmapListFilters, selectedType?: SurveySubjectTypes) => {
    const attributeId = params && params.attribute ? params.attribute : null;
    const tmpParams = { ...params, sort: `dim.${props.contentLocale}` };
    if (attributeId) {
      if (props.abortController) {
        props.abortController.abort('superseded by new request');
      }
      return props.fetchMatrix(attributeId, selectedType || selectedSurveyType, tmpParams);
    }
    return Promise.resolve();
  };

  const [entityLoading, setEntityLoading] = useState(false);

  const setAttributeFilter = async (attributeId: string) => {
    setEntityLoading(true);
    const newFilters = {
      ...filterState,
      attribute: attributeId,
      selectedGroup: null,
      filterTopicId: null,
      id: '1',
    };
    setFilterState(newFilters);
    updateUrl(newFilters);
    await fetchContent(newFilters);
    setEntityLoading(false);
  };

  const selectPeriod = (period: PeriodValues) => {
    const newFilters = { ...filterState, filterAnsweredRange: period };
    setFilterState(newFilters);
    props.changeSelectedPeriod(period);
    updateUrl(newFilters);
    fetchContent(newFilters, selectedSurveyType);
  };

  const changeSelectedSurvey = (
    surveyGroup: string | null,
    surveyPulse: string | null,
    surveyPeople: string | null,
    activeSelector: SurveySubjectTypes,
  ) => {
    const newFilters = {
      ...filterState,
      surveyGroup,
      surveyPulse,
      surveyPeople,
      filterTopicId: null,
    };
    if (selectedSurveyType !== activeSelector) {
      newFilters.selectedGroup = null;
    }
    setFilterState(newFilters);
    setSelectedSurveyType(activeSelector);
    updateUrl(newFilters);
    fetchContent(newFilters, activeSelector);
    props.setSurvey(newFilters.surveyPeople || newFilters.surveyGroup || newFilters.surveyPulse || undefined);
  };

  useEffect(() => {
    props.setSurvey(
      filterState.surveyPeople || filterState.surveyGroup || filterState.surveyPulse || undefined,
    );
  }, [filterState.surveyPeople, filterState.surveyGroup, filterState.surveyPulse]);

  const changeFilter = (val: string | null, type: FilterIds) => {
    const newFilters = { ...filterState, [type]: val };
    setFilterState(newFilters);
    updateUrl(newFilters);
    return fetchContent(newFilters);
  };

  const changePage = (page: number) => {
    changeFilter(page.toString(), 'page');
  };

  const selectGroup = async (selectedGroup: string | null) => {
    setEntityLoading(true);
    const res = await changeFilter(selectedGroup, 'selectedGroup');
    setEntityLoading(false);
    return res;
  };

  const selectTopic = (filterTopicId: string | null) => {
    return changeFilter(filterTopicId, 'filterTopicId');
  };

  const initializeFromUrl = (): HeatmapListFilters => {
    const searchStrObj: Partial<HeatmapListFilters> = queryString.parse(props.location.search);
    const defaultFilters = {
      attribute: null,
      surveyPulse: null,
      surveyGroup: null,
      surveyPeople: null,
      selectedGroup: null,
      filterTopicId: null,
      filterAnsweredRange: null,
      page: '1',
      id: '2',
      ...searchStrObj,
    };
    setFilterState(defaultFilters);
    updateUrl(defaultFilters);
    return defaultFilters;
  };

  const updateUrl = (queryParamsObj: { [key: string]: string | null }) => {
    props.history.replace({ search: getUrlFromObject(queryParamsObj) });
  };

  const getMultiAnswerScaleTopics = (topics: ESTopic[], scales: Scale[]) => {
    const oneAnswerOptionScaleId = scales
      .map((scale) => {
        return scale.labels.length === 1 ? scale.versionId : undefined;
      })
      .filter(el => el !== undefined);

    return topics.filter(topic => !oneAnswerOptionScaleId.includes(topic.scale_version_id));
  };

  const result = props.result;
  let topics = getMultiAnswerScaleTopics(props.topics, props.scales);

  if (!filterState.surveyPulse && !filterState.surveyGroup && !filterState.surveyPeople && result) {
    const tmp: { [key: string]: ESQuestion | ESTopic } = {};
    let usedValIds: string[] = [];
    for (const agg of Object.keys(result.aggrBySegmentId)) {
      usedValIds = usedValIds.concat(Object.keys(result.aggrBySegmentId[agg].valById));
    }
    const topRowTopics = props.topRow && props.topRow.agg && props.topRow.agg.topics;
    if (topRowTopics) {
      usedValIds = usedValIds.concat(topRowTopics.map(t => t.id));
    }
    usedValIds = Array.from(new Set(usedValIds));
    Object.keys(result.valueById || {}).forEach((k) => {
      if (usedValIds.includes(k) || filterState.filterTopicId) {
        // Hide empty columns only on topic level
        tmp[k] = result.valueById[k];
      }
    });
    topics = topics.filter(t => usedValIds.includes(t.id) || t.id === filterState.filterTopicId);

    result.valueById = tmp;
  }

  return (
    <>
      {initialLoading ? (
        <GlobalSpinnerContainer>
          <LoadingSpinner />
        </GlobalSpinnerContainer>
      ) : (
        <>
          {!props.optionAttributes.length ? (
            fetchingError ? (
              <TableError t={props.t} />
            ) : (
              <NoAttrsMessage>
                <span data-translation="x.compare.no_attributes.text">
                  {props.t('x.compare.no_attributes.text')}
                </span>{' '}
                <Link data-translation="x.compare.no_attributes.cta" to={routes.company.attributes}>
                  {props.t('x.compare.no_attributes.cta')}
                </Link>
              </NoAttrsMessage>
            )
          ) : (
            <>
              <Header
                t={props.t}
                setAttributeFilter={setAttributeFilter}
                changeSelectedSurvey={changeSelectedSurvey}
                initFiltersObj={filterState}
                setActiveSelector={setSelectedSurveyType}
                activeSelector={selectedSurveyType}
                selectPeriod={selectPeriod}
              />
              {result ? (
                <CompareContainer>
                  <RatingTable
                    topics={topics}
                    entityLoading={entityLoading}
                    result={result}
                    changePage={changePage}
                    filterState={filterState}
                    selectGroup={selectGroup}
                    selectTopic={selectTopic}
                    selectedAttributeObj={
                      filterState.attribute ? props.attributesById[filterState.attribute] : null
                    }
                    selectedSurveyType={selectedSurveyType}
                    selectedPeriod={filterState.filterAnsweredRange || PeriodValues.AllTime}
                  />
                </CompareContainer>
              ) : (
                props.error && <TableError t={props.t} />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch: ThunkMWDispatch): DispatchProps => ({
  fetchMatrix: (attributeId, surveyType, params) =>
    fetchMatrixDistributor(dispatch, attributeId, surveyType, params),
  fetchTopicById: groupId => dispatch(fetchTopicById(groupId)),
  fetchScales: params => dispatch(fetchScales(params)),
  fetchAttrs: params => dispatch(fetchAttributes(params)),
  changeSelectedPeriod: (period: PeriodValues) => dispatch(changeSelectedPeriod(period)),
  setSurvey: (survey?: string) => dispatch(setMatrixSurvey(survey)),
});

const mapStateToProps = (state: StoreI): StateProps => ({
  contentLocale: getContentLocale(state),
  attributesById: state.attributes.byId,
  optionAttributes: state.attributes.items,
  result: state.heatmap.result,
  topRow: state.heatmap.topRow,
  loadingTable: state.heatmap.loadingTable,
  error: state.heatmap.error,
  topics: state.heatmap.topics || [],
  scales: state.scales.items || [],
  abortController: state.heatmap.abortController,
});

const HeatmapS = connect(mapStateToProps, mapDispatchToProps)(Heatmap);
const HeatmapT = withTranslation()(HeatmapS);

export default HeatmapT;
